/**
 * OperationalBundleSignatureBadge
 *
 * Compact cryptographic status indicator for bundle signatures.
 *
 * Color semantics:
 * - Green: verified signature
 * - Yellow: unsigned bundle
 * - Red: invalid / tampered signature
 */

import React from 'react';
import type { OperationalBundleV1 } from '../../export/operationalBundleTypes.js';
import type { AttestationVerificationResult } from '../operationalBundleAttestationTypes.js';

interface OperationalBundleSignatureBadgeProps {
  bundle: OperationalBundleV1;
  attestationResult?: AttestationVerificationResult | null;
}

export const OperationalBundleSignatureBadge: React.FC<OperationalBundleSignatureBadgeProps> = ({
  bundle,
  attestationResult,
}) => {
  const isSigned = !!bundle.signature;

  if (!isSigned) {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 700,
        backgroundColor: '#f59e0b20',
        color: '#f59e0b',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}>
        UNSIGNED
      </span>
    );
  }

  if (!attestationResult) {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 700,
        backgroundColor: '#f59e0b20',
        color: '#f59e0b',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}>
        SIGNATURE PRESENT — UNVERIFIED
      </span>
    );
  }

  if (attestationResult.valid) {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 700,
        backgroundColor: '#22c55e20',
        color: '#22c55e',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}>
        <span>VERIFIED SIGNATURE</span>
        <span style={{ fontSize: 9, opacity: 0.7 }}>
          {bundle.signingAlgorithm}
        </span>
      </span>
    );
  }

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: 3,
      fontSize: 10,
      fontWeight: 700,
      backgroundColor: '#ef444420',
      color: '#ef4444',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}>
      INVALID SIGNATURE
    </span>
  );
};
